"use client";

import { Checkbox, cn, Table } from "@kyan/ui";

import { ColumnsDropdown } from "./components/ColumnsDropdown";
import { ErrorState } from "./components/ErrorState";
import { OptionChainGroupEffect } from "./components/OptionChainGroupEffect";
import { OptionChainHeaderGroup } from "./components/OptionChainHeaderGroup";
import { OptionTypeSwitch } from "./components/OptionTypeSwitch";
import { PriceIndicator } from "./components/PriceIndicator";
import { TradeDirectionSwitch } from "./components/TradeDirectionSwitch";
import { fallbackOptionchainData } from "./OptionChain.constants";
import { useOptionChainController } from "./OptionChain.controller";
import {
  getOptionChainGroupId,
  getPriceIndicatorRenderConditions,
} from "./OptionChain.utils";

export const OptionChain: React.FC = () => {
  const {
    dist,
    market,
    maturity,
    hasError,
    aroundATM,
    isLoading,
    shownColumns,
    activeGroupIds,
    filteredColumns,
    optionChainData,
    marketIndexPrice,
    setDist,
    setAroundATM,
    onGroupClick,
    setShownColumns,
  } = useOptionChainController();

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 px-3 sm:px-6">
        <OptionTypeSwitch />
        <TradeDirectionSwitch />
        <ColumnsDropdown value={shownColumns} onChange={setShownColumns} />
        <Checkbox
          value={aroundATM}
          label="Around ATM"
          onValueChange={setAroundATM}
        />
        <Checkbox label="Dist" value={dist} onValueChange={setDist} />
      </div>

      <Table
        isLoading={isLoading}
        columns={filteredColumns}
        activeGroupIds={activeGroupIds}
        bodyEffectElement={hasError && <ErrorState />}
        headerGroupElement={<OptionChainHeaderGroup />}
        data={hasError ? fallbackOptionchainData : optionChainData}
        classes={{
          group: "group",
          wrapper: "pt-4 pb-3 bg-transparent",
          table:
            "bg-white dark:bg-background shadow-[0px_0px_14px_0px_rgba(26,26,26,0.10)]",
          columnClassName: (_column, columnIndex) =>
            cn("justify-center", {
              "lg:justify-end": columnIndex < filteredColumns.length / 2 - 1,
              "lg:justify-start": columnIndex > filteredColumns.length / 2,
            }),
          cellClassName: ({ cellIndex }) =>
            cn("justify-center", {
              "lg:justify-end": cellIndex < filteredColumns.length / 2 - 1,
              "lg:justify-start": cellIndex > filteredColumns.length / 2,
            }),
          tbody: "relative",
          loading: {
            skeleton: "h-[34px]",
          },
        }}
        onGroupClick={onGroupClick}
        renderGroupEffect={({ group, isActive }) => (
          <OptionChainGroupEffect group={group} isActive={isActive} />
        )}
        getGroupId={(row, group, rowIndex) =>
          getOptionChainGroupId({
            row,
            group,
            maturity: maturity.toUpperCase(),
            rowIndex,
            marketPair: market.pair,
          })
        }
        renderRowEffect={({ row, rowIndex }) => {
          const { shouldRender, attachToBottom } =
            getPriceIndicatorRenderConditions({
              data: optionChainData,
              row,
              rowIndex,
              marketIndexPrice,
            });

          return (
            shouldRender && <PriceIndicator attachToBottom={attachToBottom} />
          );
        }}
      />
    </>
  );
};
